import { RefreshTokenResponseDto } from '@diritto/common/account/dtos/refresh-token.response.dto';
import { removeNull } from '@diritto/common/utils/array';
import { assert } from '@diritto/common/utils/assert';
import { getNewCookieStr } from '@diritto/common/utils/cookie';
import { ReviveDateTime } from '@diritto/common/utils/date';
import { isUnauthorizedError } from '@diritto/common/utils/requester/error';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

export type TokenPayload = {
  accessToken?: string;
  refreshToken?: string;
};

export type HttpOptions = {
  needReviveDate?: boolean;
  refreshTokenUrl?: string;
  token?: TokenPayload;
  interceptorError?: (error: any) => any;
};

export class Http {
  protected instance!: AxiosInstance;

  constructor(protected options: HttpOptions = {}) {
    this.updateOptions(options);
  }

  updateOptions(options: HttpOptions) {
    this.options = options;
    const tokenCookie = getNewCookieStr(
      removeNull([
        this.options.token?.accessToken ? { key: 'at', value: this.options.token.accessToken } : undefined,
        this.options.token?.refreshToken ? { key: 'rt', value: this.options.token.refreshToken } : undefined,
      ]),
    );

    this.instance = axios.create({
      timeout: 30 * 1000,
      headers: {
        'content-type': 'application/json',
        ...(tokenCookie ? { cookie: tokenCookie } : {}),
      },
      transformResponse: res => {
        try {
          if (this.options.needReviveDate) {
            return JSON.parse(res, ReviveDateTime);
          }
          return JSON.parse(res);
        } catch (e) {
          return res;
        }
      },
      responseType: 'json',
      withCredentials: true,
    });

    this.instance.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status) {
          return this.options.interceptorError?.(error) ?? Promise.reject(error);
        }
        return Promise.reject(error);
      },
    );
  }

  getInstance() {
    return this.instance;
  }

  setToken(token: TokenPayload) {
    this.updateOptions({ ...this.options, token });
  }

  get<ResponseType>(url: string, options?: AxiosRequestConfig<ResponseType>): Promise<AxiosResponse<ResponseType>> {
    return this.refreshIfUnauthorized<ResponseType>(
      async (opts?: AxiosRequestConfig) =>
        this.instance.get<ResponseType, AxiosResponse<ResponseType>, ResponseType>(url, opts),
      options,
    );
  }

  post<ParamType, ResponseType>(
    url: string,
    param?: ParamType,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType>> {
    return this.refreshIfUnauthorized<ResponseType>(
      async (opts?: AxiosRequestConfig) =>
        this.instance.post(url, param, {
          ...opts,
          headers: { ...opts?.headers },
        }),
      options,
    );
  }

  put<ParamType, ResponseType>(
    url: string,
    param: ParamType,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType>> {
    return this.refreshIfUnauthorized<ResponseType>(
      async (opts?: AxiosRequestConfig) =>
        this.instance.put(url, param, {
          ...opts,
          headers: { ...opts?.headers },
        }),
      options,
    );
  }

  patch<ParamType, ResponseType>(
    url: string,
    param: ParamType,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType>> {
    return this.refreshIfUnauthorized<ResponseType>(
      async (opts?: AxiosRequestConfig) =>
        this.instance.patch(url, param, {
          ...opts,
          headers: { ...opts?.headers },
        }),
      options,
    );
  }

  delete<ResponseType>(url: string, options?: AxiosRequestConfig): Promise<AxiosResponse<ResponseType>> {
    return this.refreshIfUnauthorized<ResponseType>(
      async () =>
        this.instance.delete(url, {
          ...options,
          headers: { ...options?.headers },
        }),
      options,
    );
  }

  async refreshToken(options?: AxiosRequestConfig): Promise<AxiosResponse<RefreshTokenResponseDto>> {
    assert(this.options.refreshTokenUrl);
    return this.instance.post(
      this.options.refreshTokenUrl,
      {},
      {
        ...options,
        headers: { ...options?.headers },
      },
    );
  }

  private async refreshIfUnauthorized<ResponseType>(
    handler: (opts?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseType>>,
    options?: AxiosRequestConfig,
  ) {
    try {
      return await handler(options);
    } catch (error) {
      if (isUnauthorizedError(error) && this.options.refreshTokenUrl) {
        const { data } = await this.refreshToken(options);
        const newCookie = getNewCookieStr(
          [{ key: 'at', value: data.token.accessToken }],
          (options?.headers?.cookie as string) ?? '',
        );
        const newOptions = { ...options, headers: { ...options?.headers, cookie: newCookie } };
        return await handler(newOptions);
      }
      throw error;
    }
  }
}
